import { config } from "@/config";
import { logger } from "@/utils/logger/logger";

export const getAuthenticationPageUrl = (): string | undefined => {
  const base = config.frontendBaseUrl?.replace(/\/$/, "");
  if (!base) return undefined;
  return `${base}/autentisering`;
};

const CANCEL_URL_METADATA_KEY = "cancel_url";
const HIDE_CLOSE_BUTTON_METADATA_KEY = "hide_close_button";

interface MetadataSource {
  metadata?: { [key: string]: string } | null;
}

/** Organization metadata > Client (Application) metadata. */
export const getMetadataCancelUrl = ({
  organization,
  client,
}: {
  organization?: MetadataSource | null;
  client?: MetadataSource | null;
}): string | undefined => {
  const orgValue = organization?.metadata?.[CANCEL_URL_METADATA_KEY]?.trim();
  if (orgValue) return orgValue;

  const clientValue = client?.metadata?.[CANCEL_URL_METADATA_KEY]?.trim();
  if (clientValue) return clientValue;

  return undefined;
};

/**
 * Resolves the URL a screen's close ("X") button should send a user to when
 * they cancel: Organization metadata > Client metadata > static
 * authentication-page fallback. Named distinctly from "return"/"callback" URL
 * terminology, since this is unrelated to the OAuth redirect_uri/callback -
 * it's just a plain page for someone bailing out before completing anything.
 *
 * Returns undefined (which CloseButton already renders as nothing) when the
 * calling Application has hide_close_button metadata set to "true" - e.g.
 * the Thon Hotels native app, which has nowhere sensible to send a browser
 * redirect back to.
 */
export const getCancelUrl = (params: {
  organization?: MetadataSource | null;
  client?: MetadataSource | null;
}): string | undefined => {
  const isHiddenByClient = params.client?.metadata?.[HIDE_CLOSE_BUTTON_METADATA_KEY] === "true";
  if (isHiddenByClient) return undefined;

  return getMetadataCancelUrl(params) ?? getAuthenticationPageUrl();
};

const LOGIN_URL_METADATA_KEY = "login_url";

/** Organization metadata > Client (Application) metadata. */
export const getMetadataLoginUrl = ({
  organization,
  client,
}: {
  organization?: MetadataSource | null;
  client?: MetadataSource | null;
}): string | undefined => {
  const orgValue = organization?.metadata?.[LOGIN_URL_METADATA_KEY]?.trim();
  if (orgValue) return orgValue;

  const clientValue = client?.metadata?.[LOGIN_URL_METADATA_KEY]?.trim();
  if (clientValue) return clientValue;

  return undefined;
};

/**
 * Resolves the URL the reset-password-success screen's "go to login" button
 * should send a user to: Organization metadata > Client metadata. Unlike
 * getCancelUrl, this does NOT fall back to the generic authentication page -
 * that page isn't a login destination, so showing the button pointed at it
 * would be misleading. The button should simply not render when no login_url
 * metadata is configured.
 */
export const getLoginUrl = (params: {
  organization?: MetadataSource | null;
  client?: MetadataSource | null;
}): string | undefined => {
  return getMetadataLoginUrl(params);
};

/**
 * Takes a URL (absolute or relative) and rebases it to the current window's origin,
 * preserving the path and query parameters.
 */
export const rebaseLinkToCurrentOrigin = (
  originalLink: string | undefined | null,
): string | undefined | null => {
  if (typeof window === "undefined" || !originalLink) {
    return originalLink;
  }

  const targetOrigin = window.location.origin;

  try {
    // Handle absolute URLs
    if (originalLink.startsWith("http://") || originalLink.startsWith("https://")) {
      const url = new URL(originalLink);
      return targetOrigin + url.pathname + url.search + url.hash;
    }

    // Handle root-relative URLs (starting with /)
    if (originalLink.startsWith("/")) {
      const url = new URL(originalLink, targetOrigin);
      return targetOrigin + url.pathname + url.search + url.hash;
    }

    // Handle relative URLs (no leading slash)
    // Treat as relative to current path
    const url = new URL(originalLink, window.location.href);
    return targetOrigin + url.pathname + url.search + url.hash;
  } catch (error) {
    logger.error("Failed to rebase URL", error);
    return originalLink;
  }
};

/**
 * Resolves a path under public/ (e.g. "/illustrations/foo.svg") to an absolute
 * URL on the origin this bundle was actually loaded from. Auth0 ACUL renders
 * our JS inside its own hosted page (e.g. a custom domain like
 * dev-auth.thon.no), not our index.html - so a root-relative src would
 * otherwise resolve against Auth0's origin and 404. Mirrors the favicon fix
 * in OrganizationContext.tsx.
 */
export const getPublicAssetUrl = (path: string): string => {
  try {
    return new URL(path, import.meta.url).href;
  } catch {
    return path;
  }
};
